import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, MapPin, Loader2, MapPinned } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { PropertyFilters } from '@/types/property';
import { Checkbox } from '@/components/ui/checkbox';
import { ARCHITECTURAL_STYLE_OPTIONS, PROPERTY_CONDITION_OPTIONS } from '@/lib/propertyStyleFilters';
import { toast } from 'sonner';
import { LocationFallbackDialog } from './LocationFallbackDialog';
import { SearchInputWithSuggestions, type Suggestion } from './SearchInputWithSuggestions';
import { useNeighborhoodsIndex } from '@/hooks/useNeighborhoods';
import { usePropertiesCount } from '@/hooks/useProperties';
import { useAddressSuggestions } from '@/hooks/useAddressSuggestions';
import { useFilteredNeighborhoodCounts } from '@/hooks/useFilteredNeighborhoodCounts';
import { useCity } from '@/contexts/CityContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const PROPERTY_TYPES = ['House', 'Condo', 'Townhouse', 'Multi-Family', 'Land'];

const PRICE_RANGES = [
  { label: 'Any Price', min: undefined, max: undefined },
  { label: 'Under $300K', min: 0, max: 300000 },
  { label: '$300K - $400K', min: 300000, max: 400000 },
  { label: '$400K - $500K', min: 400000, max: 500000 },
  { label: '$500K - $600K', min: 500000, max: 600000 },
  { label: '$600K - $700K', min: 600000, max: 700000 },
  { label: '$700K - $800K', min: 700000, max: 800000 },
  { label: '$800K - $1M', min: 800000, max: 1000000 },
  { label: '$1M - $1.5M', min: 1000000, max: 1500000 },
  { label: 'Over $1.5M', min: 1500000, max: undefined },
];

const SQFT_RANGES = [
  { label: 'Any Size', min: undefined, max: undefined },
  { label: 'Under 1,000 sqft', min: undefined, max: 1000 },
  { label: '1,000 - 1,500 sqft', min: 1000, max: 1500 },
  { label: '1,500 - 2,000 sqft', min: 1500, max: 2000 },
  { label: '2,000 - 3,000 sqft', min: 2000, max: 3000 },
  { label: '3,000 - 4,000 sqft', min: 3000, max: 4000 },
  { label: 'Over 4,000 sqft', min: 4000, max: undefined },
];

const YEAR_BUILT_RANGES = [
  { label: 'Any Year', min: undefined, max: undefined },
  { label: '2023 or newer', min: 2023, max: undefined },
  { label: '2020 or newer', min: 2020, max: undefined },
  { label: '2010 - 2019', min: 2010, max: 2019 },
  { label: '2000 - 2009', min: 2000, max: 2009 },
  { label: '1990 - 1999', min: 1990, max: 1999 },
  { label: '1980 - 1989', min: 1980, max: 1989 },
  { label: 'Before 1980', min: undefined, max: 1979 },
];

const PARKING_TYPES = ['Garage', 'Double Garage', 'Triple Garage', 'Carport', 'Street', 'Underground'];

const BEDROOMS = ['Any', '1+', '2+', '3+', '4+', '5+'];
const BATHROOMS = ['Any', '1+', '2+', '3+', '4+'];

interface SearchBarProps {
  variant?: 'hero' | 'compact';
  initialFilters?: PropertyFilters;
  onSearch?: (filters: PropertyFilters) => void;
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
  /** Extra element rendered inline next to the search input (compact mode only) */
  extraAction?: React.ReactNode;
}

export function SearchBar({ variant = 'compact', initialFilters, onSearch, filtersOpen: controlledFiltersOpen, onFiltersOpenChange, extraAction }: SearchBarProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(initialFilters?.searchQuery || searchParams.get('q') || '');
  const [filters, setFilters] = useState<PropertyFilters>(initialFilters || {});
  // Auto-open filter panel when coming from "Advanced Search" link
  const [internalFiltersOpen, setInternalFiltersOpen] = useState(searchParams.get('advanced') === 'true');
  
  const filtersOpen = controlledFiltersOpen ?? internalFiltersOpen;
  const setFiltersOpen = onFiltersOpenChange ?? setInternalFiltersOpen;
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  
  // Get current city from context
  const { city: selectedCity, setCity } = useCity();

  // Real-time property count based on current filters
  const liveFilters = useMemo(() => ({
    ...filters,
    city: query.trim() ? filters.city : (filters.city || selectedCity),
    searchQuery: query || undefined,
  }), [filters, query, selectedCity]);
  
  // Debounced so dragging sliders / typing triggers one count query, not dozens.
  const debouncedLiveFilters = useDebouncedValue(liveFilters, 300);
  const { data: propertyCount, isLoading: isCountLoading } = usePropertiesCount(debouncedLiveFilters);

  // Fetch neighborhoods from database
  const { data: neighborhoodData } = useNeighborhoodsIndex();
  
  // Fetch address suggestions based on current query
  const { data: addressSuggestions } = useAddressSuggestions(query);

  // Fetch filtered neighborhood counts when other filters are active
  const filteredCountsFilters = useMemo(() => ({
    city: filters.city || selectedCity,
    propertyType: filters.propertyType,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    minBedrooms: filters.minBedrooms,
    minBathrooms: filters.minBathrooms,
    minSqft: filters.minSqft,
    maxSqft: filters.maxSqft,
  }), [filters, selectedCity]);
  const { data: filteredCounts } = useFilteredNeighborhoodCounts(useDebouncedValue(filteredCountsFilters, 300));

  // Sort cities so selected city appears first
  const sortedCitiesForFilter = useMemo(() => {
    if (!neighborhoodData?.sortedCities) return [];
    
    // Put selected city first, then others
    return [...neighborhoodData.sortedCities].sort((a, b) => {
      if (a === selectedCity) return -1;
      if (b === selectedCity) return 1;
      return 0;
    });
  }, [neighborhoodData?.sortedCities, selectedCity]);

  // Build suggestions list from neighborhoods, cities, postal codes, and addresses
  const suggestions = useMemo(() => {
    const items: Suggestion[] = [];
    
    // Add address suggestions first (most relevant when user is typing an address)
    if (addressSuggestions && addressSuggestions.length > 0) {
      addressSuggestions.forEach(addr => {
        items.push({
          label: addr.address,
          type: 'address',
          id: addr.slug || addr.id,
          context: addr.neighborhood ? `${addr.neighborhood}, ${addr.city}` : addr.city,
        });
      });
    }
    
    if (neighborhoodData) {
      // Add cities
      neighborhoodData.sortedCities.forEach(city => {
        items.push({ label: city, type: 'city' });
      });
      
      // Add all neighborhoods
      Object.values(neighborhoodData.grouped).forEach((neighborhoods) => {
        (neighborhoods as { neighborhood: string }[]).forEach((n) => {
          items.push({ label: n.neighborhood, type: 'neighborhood' });
        });
      });
      
      // Add postal codes
      neighborhoodData.postalCodes?.forEach(postalCode => {
        items.push({ label: postalCode, type: 'postal' });
      });
    }
    
    return items;
  }, [neighborhoodData, addressSuggestions]);

  const handleSearch = () => {
    const normalizedQuery = query.replace(/\s+/g, ' ').trim();

    // If user searched for a known city name, treat it as a city filter (not free-text)
    // IMPORTANT: on first load, neighborhoodData may not be ready yet, so keep a small
    // fallback list to ensure "Calgary" / "Edmonton" searches still work.
    const cityCandidates = neighborhoodData?.sortedCities?.length
      ? neighborhoodData.sortedCities
      : ['Edmonton', 'Calgary'];

    const matchedCity = cityCandidates.find(
      (c) => c.toLowerCase() === normalizedQuery.toLowerCase()
    );

    // Keep the app's active city in sync when the user explicitly searches a city.
    if (matchedCity && matchedCity !== selectedCity) {
      setCity(matchedCity);
    }

    const searchFilters: PropertyFilters = {
      ...filters,
      city: matchedCity ? matchedCity : (filters.city || undefined),
      searchQuery: matchedCity ? undefined : (normalizedQuery || undefined),
    };

    if (onSearch) {
      onSearch(searchFilters);
    } else {
      const params = new URLSearchParams();
      if (searchFilters.searchQuery) params.set('q', searchFilters.searchQuery);
      if (searchFilters.city) params.set('city', searchFilters.city);
      if (searchFilters.propertyType) params.set('type', searchFilters.propertyType);
      if (searchFilters.neighborhood) params.set('neighborhood', searchFilters.neighborhood);
      if (searchFilters.minBedrooms) params.set('beds', searchFilters.minBedrooms.toString());
      if (searchFilters.minBathrooms) params.set('baths', searchFilters.minBathrooms.toString());
      if (searchFilters.minPrice) params.set('minPrice', searchFilters.minPrice.toString());
      if (searchFilters.maxPrice) params.set('maxPrice', searchFilters.maxPrice.toString());
      if (searchFilters.minSqft) params.set('minSqft', searchFilters.minSqft.toString());
      if (searchFilters.maxSqft) params.set('maxSqft', searchFilters.maxSqft.toString());
      if (searchFilters.minYearBuilt) params.set('minYear', searchFilters.minYearBuilt.toString());
      if (searchFilters.maxYearBuilt) params.set('maxYear', searchFilters.maxYearBuilt.toString());
      if (searchFilters.parking) params.set('parking', searchFilters.parking);
      if (searchFilters.architecturalStyles?.length) params.set('styles', searchFilters.architecturalStyles.join(','));
      if (searchFilters.propertyConditions?.length) params.set('conditions', searchFilters.propertyConditions.join(','));
      navigate(`/search?${params.toString()}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // When user selects a suggestion, auto-apply neighborhood filter if applicable
  const handleSuggestionSelect = (suggestion: Suggestion) => {
    // Handle address suggestions - navigate directly to property page
    if (suggestion.type === 'address' && suggestion.id) {
      navigate(`/property/${suggestion.id}`);
      return;
    }
    
    if (suggestion.type === 'neighborhood') {
      // Find which city this neighborhood belongs to
      const cityForNeighborhood = neighborhoodData?.sortedCities?.find((city) =>
        neighborhoodData.grouped[city]?.some(
          (n) => n.neighborhood.toLowerCase() === suggestion.label.toLowerCase()
        )
      );
      
      if (cityForNeighborhood) {
        // Set the neighborhood filter in "City - Neighborhood" format
        setFilters({
          ...filters,
          neighborhood: `${cityForNeighborhood} - ${suggestion.label}`,
        });
        // Keep the neighborhood name visible in the input so users see their selection
        setQuery(suggestion.label);
      }
    } else if (suggestion.type === 'city') {
      // For city suggestions, update the city context
      setCity(suggestion.label);
      setFilters({ ...filters, city: suggestion.label, neighborhood: undefined });
      // Keep the city name visible in the input
      setQuery(suggestion.label);
    } else if (suggestion.type === 'postal') {
      // For postal codes, keep the value visible
      setQuery(suggestion.label);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setQuery('');
  };

  const activeFilterCount = [
    filters.city,
    filters.propertyType,
    filters.neighborhood,
    filters.minBedrooms,
    filters.minBathrooms,
    filters.minPrice || filters.maxPrice,
    filters.minSqft || filters.maxSqft,
    filters.minYearBuilt || filters.maxYearBuilt,
    filters.parking,
    filters.architecturalStyles?.length,
    filters.propertyConditions?.length,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || query;

  if (variant === 'hero') {
    return (
      <div className="bg-card text-card-foreground rounded-xl shadow-xl p-4 md:p-6 max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row gap-3">
          <SearchInputWithSuggestions
            value={query}
            onChange={setQuery}
            onSuggestionSelect={handleSuggestionSelect}
            onKeyDown={handleKeyDown}
            suggestions={suggestions}
            placeholder="Search by city, neighborhood, or address..."
            className="flex-1"
            inputClassName="h-12 text-base pl-10"
          />
          
          <Select
            value={filters.propertyType}
            onValueChange={(v) => setFilters({ ...filters, propertyType: v === '__all__' ? undefined : v })}
          >
            <SelectTrigger className="w-full md:w-36 h-12">
              <SelectValue placeholder={<span className="text-muted-foreground">All Types</span>} />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="__all__">All Types</SelectItem>
              {PROPERTY_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.minBedrooms?.toString()}
            onValueChange={(v) => setFilters({ ...filters, minBedrooms: v === '__any__' ? undefined : parseInt(v) })}
          >
            <SelectTrigger className="w-full md:w-28 h-12">
              <SelectValue placeholder={<span className="text-muted-foreground">Any Beds</span>} />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="__any__">Any Beds</SelectItem>
              {BEDROOMS.filter(bed => bed !== 'Any').map(bed => (
                <SelectItem key={bed} value={bed.replace('+', '')}>
                  {`${bed} Beds`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.minPrice || filters.maxPrice ? `${filters.minPrice ?? ''}-${filters.maxPrice ?? ''}` : undefined}
            onValueChange={(v) => {
              if (v === '__any__') {
                setFilters({ ...filters, minPrice: undefined, maxPrice: undefined });
              } else {
                const range = PRICE_RANGES.find(r => `${r.min ?? ''}-${r.max ?? ''}` === v);
                if (range) {
                  setFilters({ ...filters, minPrice: range.min, maxPrice: range.max });
                }
              }
            }}
          >
            <SelectTrigger className="w-full md:w-36 h-12">
              <SelectValue placeholder={<span className="text-muted-foreground">Any Price</span>} />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="__any__">Any Price</SelectItem>
              {PRICE_RANGES.filter(r => r.min !== undefined || r.max !== undefined).map((range) => (
                <SelectItem key={range.label} value={`${range.min ?? ''}-${range.max ?? ''}`}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.neighborhood}
            onValueChange={(v) => {
              if (v === 'nearby') {
                // Handle nearby - trigger geolocation with fallback dialog
                if (!navigator.geolocation) {
                  setLocationDialogOpen(true);
                  return;
                }
                toast.loading('Finding your location...');
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    toast.dismiss();
                    toast.success('Location found!');
                    navigate(`/search?lat=${position.coords.latitude}&lng=${position.coords.longitude}&nearby=true`);
                  },
                  (error) => {
                    toast.dismiss();
                    // Show fallback dialog instead of just an error
                    setLocationDialogOpen(true);
                  },
                  { enableHighAccuracy: true, timeout: 10000 }
                );
              } else {
                setFilters({ ...filters, neighborhood: v === '__all__' ? undefined : v });
              }
            }}
          >
            <SelectTrigger className="w-full md:w-44 h-12">
              <SelectValue placeholder={<span className="text-muted-foreground">All Areas</span>} />
            </SelectTrigger>
            <SelectContent className="bg-card max-h-80">
              <SelectItem value="__all__">All Areas</SelectItem>
              <SelectItem value="nearby">
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Nearby for Sale
                </span>
              </SelectItem>
              {sortedCitiesForFilter.slice(0, 5).map((city) => {
                const neighborhoods = neighborhoodData?.grouped[city] ?? [];
                // When filters are active, sort by filtered count and hide zeros
                const sorted = filteredCounts
                  ? [...neighborhoods]
                      .map((n) => ({ ...n, filteredCount: filteredCounts.get(`${city} - ${n.neighborhood}`) ?? 0 }))
                      .filter((n) => n.filteredCount > 0)
                      .sort((a, b) => b.filteredCount - a.filteredCount)
                  : neighborhoods;

                if (sorted.length === 0) return null;

                return (
                  <SelectGroup key={city}>
                    <SelectLabel>{city}</SelectLabel>
                    {sorted.map((n) => {
                      const key = `${city} - ${n.neighborhood}`;
                      const count = 'filteredCount' in n ? (n as any).filteredCount : n.count;
                      return (
                        <SelectItem key={`hero-${city}-${n.neighborhood}`} value={key}>
                          {n.neighborhood} ({count})
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                );
              })}
            </SelectContent>
          </Select>

          <Button onClick={handleSearch} size="lg" className="h-12 px-8">
            <Search className="w-5 h-5 mr-2" />
            Search
          </Button>
        </div>

        <LocationFallbackDialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen} />
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <SearchInputWithSuggestions
        value={query}
        onChange={setQuery}
        onSuggestionSelect={handleSuggestionSelect}
        onKeyDown={handleKeyDown}
        suggestions={suggestions}
        placeholder="Search by city, neighborhood, or address..."
        className="flex-1"
      />

      {extraAction}

      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              Filters
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-6 mt-6">
            {/* Property Type */}
            <div className="space-y-2">
              <Label>Property Type</Label>
              <Select
                value={filters.propertyType}
                onValueChange={(v) => setFilters({ ...filters, propertyType: v === '__all__' ? undefined : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={<span className="text-muted-foreground">All Types</span>} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Types</SelectItem>
                  {PROPERTY_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Neighborhood */}
            <div className="space-y-2">
              <Label>Neighborhood</Label>
              <Select
                value={filters.neighborhood}
                onValueChange={(v) => setFilters({ ...filters, neighborhood: v === '__all__' ? undefined : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={<span className="text-muted-foreground">All Neighborhoods</span>} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Neighborhoods</SelectItem>
                  {sortedCitiesForFilter.map((city) => (
                    <SelectGroup key={city}>
                      <SelectLabel>{city}</SelectLabel>
                      {neighborhoodData?.grouped[city]?.map((n) => {
                        const key = `${city} - ${n.neighborhood}`;
                        const count = filteredCounts ? (filteredCounts.get(key) ?? 0) : n.count;
                        return (
                          <SelectItem key={`adv-${city}-${n.neighborhood}`} value={key}>
                            {n.neighborhood} ({count})
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price Range */}
            <div className="space-y-2">
              <Label>Price Range</Label>
              <Select
                value={`${filters.minPrice || ''}-${filters.maxPrice || ''}`}
                onValueChange={(v) => {
                  const range = PRICE_RANGES.find(r => `${r.min || ''}-${r.max || ''}` === v);
                  if (range) {
                    setFilters({ ...filters, minPrice: range.min, maxPrice: range.max });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any Price" />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_RANGES.map(range => (
                    <SelectItem key={range.label} value={`${range.min || ''}-${range.max || ''}`}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Beds & Baths */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bedrooms</Label>
                <Select
                  value={filters.minBedrooms?.toString()}
                  onValueChange={(v) => setFilters({ ...filters, minBedrooms: v === '__any__' ? undefined : parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={<span className="text-muted-foreground">Any</span>} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any</SelectItem>
                    {BEDROOMS.filter(bed => bed !== 'Any').map(bed => (
                      <SelectItem key={bed} value={bed.replace('+', '')}>
                        {bed}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Bathrooms</Label>
                <Select
                  value={filters.minBathrooms?.toString()}
                  onValueChange={(v) => setFilters({ ...filters, minBathrooms: v === '__any__' ? undefined : parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={<span className="text-muted-foreground">Any</span>} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any</SelectItem>
                    {BATHROOMS.filter(bath => bath !== 'Any').map(bath => (
                      <SelectItem key={bath} value={bath.replace('+', '')}>
                        {bath}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Square Footage */}
            <div className="space-y-2">
              <Label>Square Footage</Label>
              <Select
                value={`${filters.minSqft || ''}-${filters.maxSqft || ''}`}
                onValueChange={(v) => {
                  const range = SQFT_RANGES.find(r => `${r.min || ''}-${r.max || ''}` === v);
                  if (range) {
                    setFilters({ ...filters, minSqft: range.min, maxSqft: range.max });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any Size" />
                </SelectTrigger>
                <SelectContent>
                  {SQFT_RANGES.map(range => (
                    <SelectItem key={range.label} value={`${range.min || ''}-${range.max || ''}`}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Year Built */}
            <div className="space-y-2">
              <Label>Year Built</Label>
              <Select
                value={`${filters.minYearBuilt || ''}-${filters.maxYearBuilt || ''}`}
                onValueChange={(v) => {
                  const range = YEAR_BUILT_RANGES.find(r => `${r.min || ''}-${r.max || ''}` === v);
                  if (range) {
                    setFilters({ ...filters, minYearBuilt: range.min, maxYearBuilt: range.max });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any Year" />
                </SelectTrigger>
                <SelectContent>
                  {YEAR_BUILT_RANGES.map(range => (
                    <SelectItem key={range.label} value={`${range.min || ''}-${range.max || ''}`}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Parking</Label>
              <Select
                value={filters.parking}
                onValueChange={(v) => setFilters({ ...filters, parking: v === '__all__' ? undefined : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any Parking" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any Parking</SelectItem>
                  {PARKING_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Home Style */}
            <div className="space-y-2">
              <Label>Home Style</Label>
              <div className="grid grid-cols-2 gap-2">
                {ARCHITECTURAL_STYLE_OPTIONS.map((opt) => {
                  const checked = filters.architecturalStyles?.includes(opt.key) ?? false;
                  return (
                    <label
                      key={opt.key}
                      className="flex items-center gap-2 rounded-md border border-input p-2 cursor-pointer hover:bg-accent"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          const set = new Set(filters.architecturalStyles ?? []);
                          if (value) set.add(opt.key); else set.delete(opt.key);
                          setFilters({
                            ...filters,
                            architecturalStyles: set.size > 0 ? Array.from(set) : undefined,
                          });
                        }}
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Property Condition */}
            <div className="space-y-2">
              <Label>Condition</Label>
              <div className="grid grid-cols-1 gap-2">
                {PROPERTY_CONDITION_OPTIONS.map((opt) => {
                  const checked = filters.propertyConditions?.includes(opt.key) ?? false;
                  return (
                    <label
                      key={opt.key}
                      className="flex items-center gap-2 rounded-md border border-input p-2 cursor-pointer hover:bg-accent"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          const set = new Set(filters.propertyConditions ?? []);
                          if (value) set.add(opt.key); else set.delete(opt.key);
                          setFilters({
                            ...filters,
                            propertyConditions: set.size > 0 ? Array.from(set) : undefined,
                          });
                        }}
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>



            {/* Live count indicator */}
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              {isCountLoading ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Counting...</span>
                </div>
              ) : (
                <div>
                  <span className="text-2xl font-bold text-primary">{propertyCount?.toLocaleString() ?? 0}</span>
                  <span className="text-muted-foreground ml-2">properties found</span>
                </div>
              )}
            </div>

            <Button onClick={() => { handleSearch(); setFiltersOpen(false); }} className="w-full">
              Show {propertyCount?.toLocaleString() ?? 0} Properties
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Button onClick={handleSearch} aria-label="Search properties">
        <Search className="w-4 h-4" aria-hidden="true" />
      </Button>

      <LocationFallbackDialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen} />
    </div>
  );
}
